import { useEffect, useMemo, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { CrmScheduleService } from '../../kanban-board/services/crm-schedule.service';
import { AgendaItemResponse } from '../../kanban-board/services/responses/lead-schedule.response';
import { AgendaStyles } from '../../agenda/agenda-styles';

interface Props {
    leadId: string;
    version: number;

    /// Recebe o horário clicado na grade: marcar uma ação vira apontar onde cabe, não digitar data.
    onChooseTime: (when: Date) => void;
}

/// A agenda do dia ao lado das ações: mostra a equipe inteira e destaca o que é deste lead.
export const LeadDayAgenda = ({ leadId, version, onChooseTime }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const [day, setDay] = useState(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    });

    const [items, setItems] = useState<AgendaItemResponse[]>([]);

    useEffect(() => {
        const end = new Date(day);
        end.setHours(23, 59, 59, 999);

        new CrmScheduleService()
            .agenda(day.toISOString(), end.toISOString())
            .then((res) => setItems(res.data.data ?? []))
            // Acessório da tela: falhar aqui não pode atrapalhar a lista de ações.
            .catch(() => setItems([]));
    }, [day, version]);

    const events = useMemo(
        // Mesma regra da agenda cheia: concluído sai da lista e slot de ontem não ocupa o de amanhã.
        () => items.filter((item) => !item.closedAt).map((item) => ({
            id: item.id,
            title: item.leadName,
            start: item.scheduledAt,
            // O deste lead em destaque; o resto da equipe fica apagado, como fundo de ocupação.
            backgroundColor: item.leadId === leadId ? cc.accent : cc.surfaceBg,
            borderColor: item.leadId === leadId ? cc.accent : cc.border,
            textColor: item.leadId === leadId ? '#fff' : cc.textSecondary,
            extendedProps: item,
        })),
        [items, leadId, cc]);

    const shiftDays = (days: number) => {
        const isNew = new Date(day);
        isNew.setDate(isNew.getDate() + days);
        setDay(isNew);
    };

    const arrowSx = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 26,
        height: 26,
        borderRadius: '8px',
        cursor: 'pointer',
        color: cc.textSecondary,
        border: `1px solid ${cc.border}`,
        '&:hover': { borderColor: cc.accent, color: cc.accent },
    };

    return (
        <Box
            sx={{
                background: cc.cardBg,
                border: `1px solid ${cc.border}`,
                borderRadius: '11px',
                p: 1.75,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                <Typography
                    sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: '.06em',
                        textTransform: 'uppercase',
                        color: cc.textSecondary,
                    }}
                >
                    {t('telaLead.agendaDoDia')}
                </Typography>

                <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                    <Box
                        role="button"
                        tabIndex={0}
                        aria-label={t('telaLead.diaAnterior')}
                        onClick={() => shiftDays(-1)}
                        onKeyDown={(e) => e.key === 'Enter' && shiftDays(-1)}
                        sx={arrowSx}
                    >
                        <ChevronLeftIcon sx={{ fontSize: 17 }} />
                    </Box>
                    <Box
                        role="button"
                        tabIndex={0}
                        aria-label={t('telaLead.proximoDia')}
                        onClick={() => shiftDays(1)}
                        onKeyDown={(e) => e.key === 'Enter' && shiftDays(1)}
                        sx={arrowSx}
                    >
                        <ChevronRightIcon sx={{ fontSize: 17 }} />
                    </Box>
                </Box>
            </Box>

            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: cc.textPrimary, mb: 1 }}>
                {day.toLocaleDateString(undefined, {
                    weekday: 'long', day: '2-digit', month: '2-digit',
                })}
            </Typography>

            <AgendaStyles cc={cc}>
                <FullCalendar
                    plugins={[timeGridPlugin, interactionPlugin]}
                    initialView="timeGridDay"
                    // A data vem do estado e as setas daqui navegam: a barra do FullCalendar duplicaria.
                    initialDate={day}
                    key={day.toISOString()}
                    headerToolbar={false}
                    dayHeaders={false}
                    events={events}
                    selectable
                    select={(info) => onChooseTime(info.start)}
                    eventContent={(arg) => (
                        <Tooltip title={arg.event.title}>
                            <Box
                                sx={{
                                    px: 0.5,
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {arg.event.title}
                            </Box>
                        </Tooltip>
                    )}
                    allDaySlot={false}
                    slotMinTime="06:00:00"
                    slotMaxTime="21:00:00"
                    nowIndicator
                    height={430}
                    expandRows
                />
            </AgendaStyles>

            <Typography variant="caption" sx={{ color: cc.textMuted, mt: 1, display: 'block' }}>
                {t('telaLead.agendaDoDiaAjuda')}
            </Typography>
        </Box>
    );
};
